"use client";

import { useMotionValue, useSpring, useTransform } from "motion/react";
import { type ComponentProps, type ReactNode, useEffect, useRef } from "react";
import { cn } from "@/utils";

// deg
const MAX_TILT = 5;

export const BentoItem = ({
	className,
	title,
	image,
	description,
	...props
}: ComponentProps<"figure"> & { title: string; image: ReactNode; description: string }) => {
	const containerRef = useRef<HTMLElement>(null);
	const figureRef = useRef<HTMLElement>(null);
	const figcaptionRef = useRef<HTMLElement>(null);
	const containerRectRef = useRef<DOMRect>(undefined);

	const x = useMotionValue(0);
	const y = useMotionValue(0);

	const rotateX = useSpring(x, { stiffness: 1200, damping: 12 });
	const rotateY = useSpring(y, { stiffness: 1200, damping: 12 });
	const combinedRotate = useTransform([rotateX, rotateY], ([xVal, yVal]) => {
		return {
			xVal: Number(xVal),
			yVal: Number(yVal),
		};
	});

	const translateX = useSpring(x, { stiffness: 1200, damping: 200 });
	const translateY = useSpring(y, { stiffness: 1200, damping: 200 });
	const combinedTranslate = useTransform([translateX, translateY], ([xVal, yVal]) => {
		// Normalize the values to [-1, 1] range using Math.sin for smoother, non-linear translation
		const normX = Math.sin(Number(xVal) * (Math.PI / (2 * MAX_TILT)));
		const normY = Math.sin(Number(yVal) * (Math.PI / (2 * MAX_TILT)));
		const TRANSLATE_FACTOR = 15; // Adjust this factor as needed for your design
		return {
			xVal: normX * TRANSLATE_FACTOR,
			yVal: normY * TRANSLATE_FACTOR,
		};
	});

	useEffect(
		() =>
			combinedRotate.on("change", (latest) => {
				if (!figureRef.current) {
					return;
				}
				figureRef.current.style.transform = `rotateX(${latest.xVal}deg) rotateY(${latest.yVal}deg)`;
			}),
		[combinedRotate],
	);
	useEffect(
		() =>
			combinedTranslate.on("change", (latest) => {
				if (!figcaptionRef.current) {
					return;
				}
				figcaptionRef.current.style.transform = `translateX(${latest.yVal * 0.25}px) translateY(${-latest.xVal * 0.25}px)`;
			}),
		[combinedTranslate],
	);

	return (
		// biome-ignore lint: it's ok
		<div
			className={cn(
				"perspective-distant mx-1 border border-dashed border-neutral-200 rounded-2xl",
				className,
			)}
			ref={(node) => {
				containerRef.current = node;
				containerRectRef.current = containerRef.current?.getBoundingClientRect();
			}}
			onMouseMove={(e) => {
				if (!containerRectRef.current || !containerRef.current) {
					return;
				}
				const LARGE_BOX = 600;

				const el = e.currentTarget;
				const width = el.offsetWidth;
				const height = el.offsetHeight;
				// @todo cache containerRectRef
				const elX = e.nativeEvent.clientX - containerRef.current?.getBoundingClientRect().left;
				const elY = e.nativeEvent.clientY - containerRef.current?.getBoundingClientRect().top;

				const rateX = (elX / width - 0.5) * 2;
				const rateY = -(elY / height - 0.5) * 2;

				const relativeTiltX = (LARGE_BOX / width) * MAX_TILT;
				const relativeTiltY = (LARGE_BOX / height) * MAX_TILT;

				x.set(rateY * relativeTiltX);
				y.set(rateX * relativeTiltY);
			}}
			onMouseLeave={() => {
				rotateX.set(0);
				rotateY.set(0);
				translateX.set(0);
				translateY.set(0);
				setTimeout(() => {
					rotateX.set(0);
					rotateY.set(0);
					translateX.set(0);
					translateY.set(0);
				}, 100);
			}}
		>
			<figure
				ref={figureRef}
				className={cn(
					"flex flex-col justify-end rounded-2xl border border-neutral-200 p-4 bg-white h-full will-change-[transform] transition-transform duration-100 ease-in-out transform-gpu *:translate-z-0",
					// hide parent dashed border below this element
					// so that the parent border is only visible when hovering (tilting)
					"w-[calc(100%+2px)] -ml-px",
					"h-[calc(100%+2px)] -mt-px",
				)}
				{...props}
			>
				{image}
				<figcaption
					ref={figcaptionRef}
					className="will-change-[transform] transition-transform duration-100 ease-in-out transform-gpu"
				>
					<h3 className="mt-4 text-h6 leading-h6 tracking-h6 font-medium">{title}</h3>
					<p className="mt-2 text-pregular-xs text-pregular-500 leading-pregular tracking-pregular">
						{description}
					</p>
				</figcaption>
			</figure>
		</div>
	);
};
